import { createSlice } from '@reduxjs/toolkit';
import {
  fetchPickingOrder,
  fetchPickingQueue,
  pickLine,
  reservePickingOrder,
  scanPickingCode,
  shortPickLine,
  unpickLine,
} from './operatorPickingThunks';
import { pickingLineSwitched } from './operatorPickingActions';
import { PICKING_STEP, canChooseLine, firstTarget, nextLineId, pendingOf } from './operatorPickingUtils';
import { describeCandidates } from '../operator/operatorCandidates';
import { OPERATOR_ERROR, isRetryable } from '../operator/operatorErrors';
import { intentOf } from '../operator/operatorIdempotency';
import { acceptedCode } from '../operator/operatorScanGate';
import { operatorWarehouseSelected } from '../operator/operatorSlice';
import { isBackgroundRequest } from '../feedback/feedbackSlice';

/*
 * Picking on the floor: the queue of orders, and the walk through one of them
 * — the line on screen, where it is being taken from, and what the server is
 * waiting for an answer on.
 */
/** `receivedAt`: when its first page last came, by the phone's clock. */
const initialQueue = {
  items: [],
  count: 0,
  page: 1,
  hasMore: false,
  loading: false,
  error: null,
  requestId: null,
  receivedAt: null,
};

const initialWalk = {
  lineId: null,
  step: PICKING_STEP.SLOT,
  /** The suggestion (or scanned slot) being taken from. */
  target: null,
  suggested: true,
  /** `{code, factor}`: the scanned product, in the unit of its code. */
  product: null,
  skipped: [],
};

/** One visit to an order: opening another starts it over, and a trip to the queue does not. */
const initialTask = {
  orderId: null,
  order: null,
  loading: false,
  error: null,
  requestId: null,
  walk: initialWalk,
  scan: { lastCode: null },
  pending: null,
  resolution: null,
  /** `{type, arg, key}` of the write this visit sent last: the only one whose answer it takes. */
  lastWrite: null,
};

const initialState = {
  queue: initialQueue,
  /** The reference of each order loaded here, by id: how a band names it once it is off screen. */
  names: {},
  ...initialTask,
};

/**
 * Whether an answer is to the write this visit waits for. Answers of another
 * order, or of an earlier visit to this one, are not: the operator can leave
 * an order with a write out, and its answer comes back late.
 */
const isAwaited = (state, action) => action.meta.requestId === state.lastWrite?.key;

/** Puts the walk on a line that can still be picked, or ends it. */
const placeWalk = (state) => {
  const lines = state.order?.lines || [];
  const current = lines.find((line) => line.line_id === state.walk.lineId);
  if (current && pendingOf(current) > 0 && firstTarget(current) && state.walk.step !== PICKING_STEP.DONE) return;

  const next = nextLineId(lines, state.walk.skipped);
  state.walk = {
    ...initialWalk,
    skipped: state.walk.skipped,
    lineId: next,
    step: next === null ? PICKING_STEP.DONE : PICKING_STEP.SLOT,
  };
};

/** The answer to a write carries the line and the order header, updated. */
const applyWriteAnswer = (state, payload) => {
  if (!state.order) return;
  if (payload.order) state.order = { ...state.order, ...payload.order, lines: state.order.lines };
  if (payload.line) {
    state.order.lines = state.order.lines.map((line) => (line.line_id === payload.line.line_id ? payload.line : line));
  }
};

/** After a write on the line on screen: go on from its next place, or to the next line. */
const continueAfter = (state) => {
  const line = state.order.lines.find((item) => item.line_id === state.walk.lineId);
  if (line && pendingOf(line) > 0 && firstTarget(line)) {
    state.walk = { ...state.walk, step: PICKING_STEP.SLOT, target: null, product: null, suggested: true };
    return;
  }
  state.walk = { ...state.walk, step: PICKING_STEP.SLOT, target: null, product: null };
  state.walk.lineId = null;
  placeWalk(state);
};

const settleWrite = (state) => {
  state.pending = null;
  state.lastWrite = null;
};

const rejectWrite = (state, action, type) => {
  if (action.meta.condition || !isAwaited(state, action)) return;
  state.pending = null;
  const error = action.payload;
  if (error?.kind === OPERATOR_ERROR.AMBIGUOUS) {
    state.resolution = {
      kind: 'ambiguous',
      origin: 'server',
      type,
      message: error.message,
      ...describeCandidates(error.candidates),
      arg: intentOf(action.meta.arg),
      key: action.meta.requestId,
    };
  }
  if (!isRetryable(error)) state.lastWrite = null;
};

const startWrite = (pending, type) => (state, action) => {
  // A write of another order — sent again from its band — leaves this visit alone.
  if (action.meta.arg?.orderId !== state.orderId) return;
  state.pending = pending;
  state.resolution = null;
  state.lastWrite = { type, arg: intentOf(action.meta.arg), key: action.meta.requestId };
};

const operatorPickingSlice = createSlice({
  name: 'operatorPicking',
  initialState,
  reducers: {
    pickingEntered: (state, action) => {
      if (state.orderId !== action.payload) Object.assign(state, initialTask, { orderId: action.payload });
      state.resolution = null;
    },
    pickingLeft: (state) => {
      state.resolution = null;
    },
    /** One step back: from the quantity to the product, from the product to the slot. */
    pickingStepBack: (state) => {
      if (state.walk.step === PICKING_STEP.QUANTITY) {
        state.walk.step = PICKING_STEP.PRODUCT;
        state.walk.product = null;
      } else if (state.walk.step === PICKING_STEP.PRODUCT) {
        state.walk.step = PICKING_STEP.SLOT;
        state.walk.target = null;
      }
    },
    /** Leaves the line for the end of the walk. */
    pickingLineSkipped: (state) => {
      if (state.walk.lineId === null) return;
      state.walk.skipped = [...state.walk.skipped.filter((id) => id !== state.walk.lineId), state.walk.lineId];
      state.walk.lineId = null;
      placeWalk(state);
    },
    /** Goes to the line the operator tapped, from where to take it. */
    pickingLineChosen: (state, action) => {
      const line = (state.order?.lines || []).find((item) => item.line_id === action.payload);
      const walking = [PICKING_STEP.SLOT, PICKING_STEP.PRODUCT].includes(state.walk.step);
      if (!line || state.pending || state.resolution || !walking || !canChooseLine(line, state.walk)) return;
      state.walk = {
        ...initialWalk,
        skipped: state.walk.skipped.filter((id) => id !== line.line_id),
        lineId: line.line_id,
      };
    },
    pickingResolutionDismissed: (state) => {
      state.resolution = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(operatorWarehouseSelected, () => initialState)

      .addCase(fetchPickingQueue.pending, (state, action) => {
        state.queue.requestId = action.meta.requestId;
        if (isBackgroundRequest(action)) return;
        state.queue.loading = true;
        state.queue.error = null;
      })
      .addCase(fetchPickingQueue.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.queue.requestId) return;
        const page = action.meta.arg?.page ?? 1;
        const results = action.payload.results || [];
        state.queue.loading = false;
        state.queue.error = null;
        state.queue.page = page;
        state.queue.items = page === 1 ? results : [...state.queue.items, ...results];
        state.queue.count = action.payload.count ?? state.queue.items.length;
        state.queue.hasMore = Boolean(action.payload.next);
        if (page === 1) state.queue.receivedAt = action.meta.receivedAt;
      })
      .addCase(fetchPickingQueue.rejected, (state, action) => {
        // A refresh in the background fails in silence: the list stays as it was, and the next one tries again.
        if (action.meta.requestId !== state.queue.requestId || isBackgroundRequest(action)) return;
        state.queue.loading = false;
        state.queue.error = action.payload ?? { kind: OPERATOR_ERROR.REFUSED, message: action.error.message };
      })

      .addCase(fetchPickingOrder.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.requestId = action.meta.requestId;
      })
      .addCase(fetchPickingOrder.fulfilled, (state, action) => {
        state.names[action.meta.arg] = action.payload?.reference_id || null;
        if (action.meta.requestId !== state.requestId) return;
        state.loading = false;
        state.order = action.payload;
        placeWalk(state);
      })
      .addCase(fetchPickingOrder.rejected, (state, action) => {
        if (action.meta.requestId !== state.requestId) return;
        state.loading = false;
        state.error = action.payload ?? { kind: OPERATOR_ERROR.REFUSED, message: action.error.message };
      })

      .addCase(scanPickingCode.fulfilled, (state, action) => {
        state.scan.lastCode = acceptedCode(action);
        const result = action.payload;
        if (result.kind === 'slot') {
          state.walk.target = result.target;
          state.walk.suggested = result.suggested;
          state.walk.step = PICKING_STEP.PRODUCT;
        } else {
          state.walk.product = result.product;
          state.walk.step = PICKING_STEP.QUANTITY;
        }
      })
      .addCase(pickingLineSwitched, (state, action) => {
        state.walk.lineId = action.payload;
      })

      .addCase(pickLine.pending, startWrite('pick', pickLine.typePrefix))
      .addCase(pickLine.fulfilled, (state, action) => {
        if (!isAwaited(state, action)) return;
        settleWrite(state);
        if (!state.order) return;
        applyWriteAnswer(state, action.payload);
        continueAfter(state);
      })
      .addCase(pickLine.rejected, (state, action) => rejectWrite(state, action, pickLine.typePrefix))

      .addCase(shortPickLine.pending, startWrite('shortPick', shortPickLine.typePrefix))
      .addCase(shortPickLine.fulfilled, (state, action) => {
        if (!isAwaited(state, action)) return;
        settleWrite(state);
        if (!state.order) return;
        applyWriteAnswer(state, action.payload);
        continueAfter(state);
      })
      .addCase(shortPickLine.rejected, (state, action) => rejectWrite(state, action, shortPickLine.typePrefix))

      .addCase(unpickLine.pending, startWrite('unpick', unpickLine.typePrefix))
      .addCase(unpickLine.fulfilled, (state, action) => {
        if (!isAwaited(state, action)) return;
        settleWrite(state);
        if (!state.order) return;
        applyWriteAnswer(state, action.payload);
        // Units given back can reopen a line the walk had finished.
        if (state.walk.step === PICKING_STEP.DONE) placeWalk(state);
      })
      .addCase(unpickLine.rejected, (state, action) => rejectWrite(state, action, unpickLine.typePrefix))

      // Reserving touches no line: the answer brings the header, and the walk stays where it was.
      .addCase(reservePickingOrder.pending, startWrite('reserve', reservePickingOrder.typePrefix))
      .addCase(reservePickingOrder.fulfilled, (state, action) => {
        if (!isAwaited(state, action)) return;
        settleWrite(state);
        applyWriteAnswer(state, action.payload);
      })
      .addCase(reservePickingOrder.rejected, (state, action) => rejectWrite(state, action, reservePickingOrder.typePrefix));
  },
});

export const {
  pickingEntered,
  pickingLeft,
  pickingStepBack,
  pickingLineSkipped,
  pickingLineChosen,
  pickingResolutionDismissed,
} = operatorPickingSlice.actions;

/** The line the walk is on. */
export const selectPickingLine = (state) => {
  const { order, walk } = state.operatorPicking;
  return (order?.lines || []).find((line) => line.line_id === walk.lineId) || null;
};

/** Whether a read code is welcome right now. */
export const selectPickingAccepting = (state) => {
  const picking = state.operatorPicking;
  return (
    Boolean(picking.order) &&
    !picking.pending &&
    !picking.resolution &&
    [PICKING_STEP.SLOT, PICKING_STEP.PRODUCT].includes(picking.walk.step)
  );
};

export default operatorPickingSlice.reducer;
